
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserPlus, Plus, Edit, Trash2, Search, Users, Eye, Upload, MessageSquare, Calendar, ArrowUpDown, Filter } from "lucide-react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { useTheme } from "../contexts/ThemeContext";
import "../global.css";
import { groupsAPI, contactsAPI } from "../services/api";
import { formatarData } from "../utils/dateFormatter";

const Groups = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [idDelete, setIdDelete] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [sortBy, setSortBy] = useState("nome");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactTempFilter, setContactTempFilter] = useState("all");
  const [contactModalPage, setContactModalPage] = useState(1);
  const [contactModalTotal, setContactModalTotal] = useState(0);
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    status: true,
    contatos_id: [],
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [consentimentoDeclarado, setConsentimentoDeclarado] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedGroupOption, setSelectedGroupOption] = useState('new');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    loadGroups();
    loadContacts();

    // Fechar dropdown ao clicar fora
    const handleClickOutside = (event) => {
      if (!event.target.closest(".contact-autocomplete")) {
        setShowContactDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadGroups = async () => {
    try {
      console.log("Buscando grupos para cliente:", user.cliente_id);
      const response = await groupsAPI.list(user.cliente_id);
      console.log("Resposta completa da API:", response);
      console.log("Grupos carregados:", response.data);
      const gruposArray = response.data?.grupos || response.data || [];
      console.log("Array de grupos:", gruposArray);
      setGroups(Array.isArray(gruposArray) ? gruposArray : []);
    } catch (error) {
      console.error("Erro detalhado ao carregar grupos:", error);
      toast.error("Erro ao carregar grupos");
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async (search = '', temperature = '', page = 1) => {
    try {
      const response = await contactsAPI.list(user.cliente_id, page, 50, search, '', temperature !== 'all' ? temperature : '');
      setContacts(response.data.messages || []);
      setContactModalTotal(response.data.quantidade || 0);
      setContactModalPage(page);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const groupData = {
        ...formData,
        cliente_id: user.cliente_id,
        usuario_id: user.id,
      };

      if (editingGroup) {
        await groupsAPI.update(editingGroup.id, formData);
        toast.success("Grupo atualizado com sucesso!");
      } else {
        await groupsAPI.create(groupData);
        toast.success("Grupo criado com sucesso!");
      }

      setShowModal(false);
      setEditingGroup(null);
      setFormData({ nome: "", descricao: "", status: true, contatos_id: [] });
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao salvar grupo");
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      nome: group.nome || "",
      descricao: group.descricao || "",
      status: group.status !== false,
      contatos_id: [],
    });

    setShowModal(true);
  };

  const handleDeleteGroup = async (id) => {
    if (idDelete) {
      try {
        await groupsAPI.delete(idDelete);
        toast.success("Grupo excluído com sucesso!");
        loadGroups();
        setDeleteModal(false);
      } catch (e) {
        toast.error("Erro ao excluir grupo");
      }
    }
  };

  const handleDelete = async (id) => {
    setDeleteModal(true);
    setIdDelete(id);
    try {
      setIdDelete(id);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleAddContacts = async () => {
    if (selectedContacts.length === 0) {
      toast.error("Selecione pelo menos um contato");
      return;
    }
    if (!selectedGroup || !selectedGroup.id) {
      toast.error("Nenhum grupo selecionado");
      return;
    }

    try {
      const contactsData = selectedContacts.map((contactId) => ({
        grupo_id: selectedGroup.id,
        contato_id: contactId,
        cliente_id: user.cliente_id,
        status: true,
      }));

  const response = await groupsAPI.addContact(contactsData);
      const { total, inserted, duplicates, details } = response.data;

      // Exibir mensagens de feedback
      if (inserted > 0) {
        toast.success(
          `${inserted} contato(s) adicionado(s) com sucesso!`
        );
      }

      if (duplicates > 0 && details?.duplicateContacts) {
        const duplicateNames = details.duplicateContacts
          .map((c) => c.nome || c.telefone)
          .join(", ");
        
        toast.warning(
          <div>
            <strong>{duplicates} contato(s) já existiam no grupo:</strong>
            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
              {duplicateNames}
            </div>
          </div>,
          { autoClose: 7000 }
        );
      }

      if (inserted === 0 && duplicates === total) {
        toast.info("Todos os contatos selecionados já estavam no grupo");
      }

      setShowContactModal(false);
      setSelectedContacts([]);
      setContactSearchTerm("");
      loadGroups();
    } catch (error) {
      console.error("Erro ao adicionar contatos:", error);
      
      // Exibir mensagem específica do backend
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          "Erro ao adicionar contatos";
      
      // Se for erro 409 (Conflict), mostrar como warning
      if (error.response?.status === 409) {
        toast.warning(errorMessage, { autoClose: 7000 });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const selectAllContacts = () => {
    const filteredContactIds = filteredContactsForModal.map((c) => c.id);
    setSelectedContacts(filteredContactIds);
  };

  const clearSelection = () => {
    setSelectedContacts([]);
  };

  const filteredGroups = (Array.isArray(groups) ? groups : [])
    .filter((group) => {
      const matchSearch = group.nome?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === "todos" || (statusFilter === "ativos" ? group.status : !group.status);
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === "nome") return (a.nome || "").localeCompare(b.nome || "");
      if (sortBy === "contatos") return (b.totalContatos || 0) - (a.totalContatos || 0);
      if (sortBy === "recente") return new Date(b.create_at) - new Date(a.create_at);
      return 0;
    });

  const filteredContactsForModal = contacts;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-nomoz-orange border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Segmentos" subtitle="Organize contatos em segmentos para enviar campanhas segmentadas">
            <button
              onClick={() => setShowImportModal(true)}
              className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? "bg-zinc-700 text-gray-300 hover:bg-zinc-600 border border-zinc-600"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Importar CSV</span>
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Novo Segmento</span>
            </button>
        </PageHeader>

        {/* Stats resumo */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total", value: groups.length, color: "blue" },
            { label: "Ativos", value: groups.filter(g => g.status).length, color: "green" },
            { label: "Inativos", value: groups.filter(g => !g.status).length, color: "red" },
            { label: "Média/segmento", value: groups.length ? Math.round(groups.reduce((sum, g) => sum + (g.totalContatos || 0), 0) / groups.length) : 0, color: "purple" },
          ].map((stat) => (
            <div key={stat.label} className={`rounded-xl p-4 ${darkMode ? "bg-zinc-800 border border-zinc-700" : "bg-white border border-gray-200"}`}>
              <p className={`text-xs font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{stat.label}</p>
              <p className={`text-2xl font-bold mt-1 text-${stat.color}-500`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Search + Filtros */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
            <input
              type="text"
              placeholder="Buscar segmentos..."
              className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm transition-colors ${
                darkMode
                  ? "bg-zinc-800 border border-zinc-700 text-white placeholder-gray-500 focus:border-nomoz-orange"
                  : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-nomoz-orange"
              } focus:outline-none focus:ring-1 focus:ring-nomoz-orange`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            {/* Filtro status */}
            <div className={`flex rounded-xl overflow-hidden border ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
              {[
                { key: "todos", label: "Todos" },
                { key: "ativos", label: "Ativos" },
                { key: "inativos", label: "Inativos" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    statusFilter === f.key
                      ? "bg-nomoz-navy text-white"
                      : darkMode
                        ? "bg-zinc-800 text-gray-400 hover:bg-zinc-700"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {/* Ordenação */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors cursor-pointer ${
                darkMode
                  ? "bg-zinc-800 border border-zinc-700 text-gray-300"
                  : "bg-white border border-gray-200 text-gray-600"
              }`}
            >
              <option value="nome">A-Z</option>
              <option value="contatos">Mais contatos</option>
              <option value="recente">Mais recente</option>
            </select>
          </div>
        </div>

        {/* Contador */}
        {(searchTerm || statusFilter !== "todos") && (
          <p className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
            {filteredGroups.length} de {groups.length} segmento{groups.length !== 1 ? "s" : ""}
          </p>
        )}

        {/* Groups Grid */}
        {filteredGroups.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border-2 border-dashed ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
            <Users className={`mx-auto h-12 w-12 mb-3 ${darkMode ? "text-zinc-600" : "text-gray-300"}`} />
            <h3 className={`text-base font-medium ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              {searchTerm ? "Nenhum segmento encontrado" : "Nenhum segmento criado"}
            </h3>
            <p className={`mt-1 text-sm ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
              {searchTerm ? "Tente outro termo de busca" : "Crie seu primeiro segmento para organizar contatos"}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Segmento
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGroups.map((group) => (
              <div
                key={group.id}
                className={`group rounded-xl border p-5 transition-all hover:shadow-md cursor-pointer ${
                  darkMode
                    ? "bg-zinc-800 border-zinc-700 hover:border-nomoz-orange/50"
                    : "bg-white border-gray-200 hover:border-green-400"
                }`}
                onClick={() => navigate(`/segmentos/${group.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {group.nome?.charAt(0)?.toUpperCase() || "S"}
                    </div>
                    <div className="min-w-0">
                      <h3 className={`text-sm font-semibold truncate ${darkMode ? "text-white" : "text-gray-900"}`}>
                        {group.nome || "Sem nome"}
                      </h3>
                      <p className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {formatarData(group.create_at)}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                    group.status
                      ? darkMode ? "bg-green-500/20 text-green-400" : "bg-blue-50 text-blue-700"
                      : darkMode ? "bg-red-500/20 text-red-400" : "bg-red-50 text-red-700"
                  }`}>
                    {group.status ? "Ativo" : "Inativo"}
                  </span>
                </div>

                {group.descricao && (
                  <p className={`text-xs mb-3 line-clamp-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {group.descricao}
                  </p>
                )}

                <div className={`flex items-center gap-1.5 py-2.5 px-3 rounded-lg mb-3 ${darkMode ? "bg-zinc-700/40" : "bg-gray-50"}`}>
                    <Users className="h-3.5 w-3.5 text-green-500" />
                    <span className={`text-sm font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>{group.totalContatos || 0}</span>
                    <span className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>contatos</span>
                    {group.semOptIn > 0 && (
                      <span className="ml-auto text-xs font-medium text-amber-500" title={`${group.semOptIn} contatos sem opt-in não receberão campanhas`}>
                        ⚠️ {group.semOptIn} sem opt-in
                      </span>
                    )}
                </div>

                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate(`/segmentos/${group.id}`)}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                      darkMode
                        ? "bg-zinc-700 text-gray-300 hover:bg-zinc-600"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Detalhes
                  </button>
                  <button
                    onClick={() => handleEdit(group)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      darkMode
                        ? "bg-zinc-700 text-blue-400 hover:bg-blue-500/20"
                        : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                    }`}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(group.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      darkMode
                        ? "bg-zinc-700 text-red-400 hover:bg-red-500/20"
                        : "bg-red-50 text-red-600 hover:bg-red-100"
                    }`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Group Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowModal(false); setEditingGroup(null); setFormData({ nome: "", descricao: "", status: true, contatos_id: [] }); }}>
            <div className={`w-full max-w-md rounded-2xl shadow-xl ${darkMode ? "bg-zinc-800" : "bg-white"}`} onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {editingGroup ? "Editar Segmento" : "Novo Segmento"}
                    </h3>
                    <p className={`text-xs mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {editingGroup ? "Atualize as informações do segmento" : "Crie um segmento para organizar contatos"}
                    </p>
                  </div>
                  <button onClick={() => { setShowModal(false); setEditingGroup(null); setFormData({ nome: "", descricao: "", status: true, contatos_id: [] }); }}
                    className={`p-1.5 rounded-lg transition-colors ${darkMode ? "hover:bg-zinc-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}>
                    <Trash2 className="h-4 w-4" style={{ display: 'none' }} /><span className="text-lg leading-none">×</span>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Nome</label>
                    <input type="text" required placeholder="Ex: Clientes VIP"
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-nomoz-orange focus:border-transparent transition-colors ${
                        darkMode ? "border-zinc-600 bg-zinc-700/50 text-white placeholder-gray-500" : "border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400"
                      }`}
                      value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Descrição</label>
                    <textarea rows={2} placeholder="Descreva o propósito deste segmento..."
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-nomoz-orange focus:border-transparent transition-colors ${
                        darkMode ? "border-zinc-600 bg-zinc-700/50 text-white placeholder-gray-500" : "border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400"
                      }`}
                      value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} />
                  </div>

                  {/* Contatos */}
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Contatos</label>
                    {formData.contatos_id.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.contatos_id.map((contactId) => {
                          const contact = contacts.find((c) => c.id === contactId);
                          return contact ? (
                            <span key={contactId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${darkMode ? "bg-green-500/20 text-green-400" : "bg-blue-50 text-blue-700"}`}>
                              {contact.nome}
                              <button type="button" onClick={() => setFormData({ ...formData, contatos_id: formData.contatos_id.filter((id) => id !== contactId) })}
                                className="hover:text-red-500 transition-colors">×</button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                    <button type="button"
                      onClick={() => { setSelectedGroup({ nome: formData.nome || "Novo Segmento" }); setSelectedContacts([...formData.contatos_id]); setShowContactModal(true); }}
                      className={`w-full px-3 py-2.5 rounded-xl border border-dashed text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                        darkMode ? "border-zinc-600 text-gray-400 hover:border-nomoz-orange hover:text-green-400" : "border-gray-300 text-gray-500 hover:border-nomoz-orange hover:text-green-600"
                      }`}>
                      <UserPlus className="h-4 w-4" />
                      {formData.contatos_id.length === 0 ? "Adicionar Contatos" : `${formData.contatos_id.length} contato(s) · Gerenciar`}
                    </button>
                  </div>

                  {/* Status toggle */}
                  <div className={`flex items-center justify-between p-3 rounded-xl ${darkMode ? "bg-zinc-700/30" : "bg-gray-50"}`}>
                    <span className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Status ativo</span>
                    <button type="button" onClick={() => setFormData({ ...formData, status: !formData.status })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${formData.status ? "bg-green-500" : darkMode ? "bg-zinc-600" : "bg-gray-300"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${formData.status ? "translate-x-5" : ""}`} />
                    </button>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button type="button"
                      onClick={() => { setShowModal(false); setEditingGroup(null); setFormData({ nome: "", descricao: "", status: true, contatos_id: [] }); setContactSearch(""); setShowContactDropdown(false); }}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        darkMode ? "bg-zinc-700 text-gray-300 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}>
                      Cancelar
                    </button>
                    <button type="submit" className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-nomoz-orange hover:bg-nomoz-orange-dark transition-colors">
                      {editingGroup ? "Salvar" : "Criar Segmento"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add Contact Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowContactModal(false); setSelectedContacts([]); setContactSearchTerm(""); }}>
            <div className={`w-full max-w-md rounded-2xl shadow-xl flex flex-col max-h-[80vh] ${darkMode ? "bg-zinc-800" : "bg-white"}`} onClick={(e) => e.stopPropagation()}>
              <div className="p-6 pb-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Selecionar Contatos</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? "bg-green-500/20 text-green-400" : "bg-blue-50 text-blue-700"}`}>
                    {selectedContacts.length} selecionado{selectedContacts.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <p className={`text-xs mb-4 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                  {selectedGroup?.nome}
                </p>
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
                  <input type="text" placeholder="Buscar contatos..."
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm ${
                      darkMode ? "bg-zinc-700/50 border border-zinc-600 text-white placeholder-gray-500" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-2 focus:ring-nomoz-orange`}
                    value={contactSearchTerm} onChange={(e) => {
                      setContactSearchTerm(e.target.value);
                      clearTimeout(window._contactModalTimer);
                      window._contactModalTimer = setTimeout(() => loadContacts(e.target.value, contactTempFilter), 400);
                    }} />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex gap-3">
                    <button onClick={selectAllContacts} className="text-xs text-green-500 hover:text-green-400 font-medium">Selecionar todos</button>
                    <button onClick={clearSelection} className="text-xs text-red-500 hover:text-red-400 font-medium">Limpar</button>
                  </div>
                  <span className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>{filteredContactsForModal.length} disponíveis</span>
                </div>
                <div className="flex gap-1.5 mt-3">
                  {[
                    { value: 'all', label: 'Todos' },
                    { value: 'quente', label: '🔥' },
                    { value: 'morno', label: '🟡' },
                    { value: 'frio', label: '🧊' },
                  ].map(t => (
                    <button key={t.value} onClick={() => { setContactTempFilter(t.value); loadContacts(contactSearchTerm, t.value); }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        contactTempFilter === t.value
                          ? t.value === 'quente' ? 'bg-red-600 text-white'
                          : t.value === 'morno' ? 'bg-yellow-600 text-white'
                          : t.value === 'frio' ? 'bg-blue-600 text-white'
                          : 'bg-nomoz-navy text-white'
                          : darkMode ? 'bg-zinc-700 text-gray-400 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`flex-1 overflow-y-auto mx-6 rounded-xl border ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
                {filteredContactsForModal.length === 0 ? (
                  <div className={`p-8 text-center ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhum contato encontrado</p>
                  </div>
                ) : (
                  filteredContactsForModal.map((contact, i) => (
                    <div key={contact.id}
                      onClick={() => toggleContactSelection(contact.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                        i > 0 ? (darkMode ? "border-t border-zinc-700/50" : "border-t border-gray-100") : ""
                      } ${selectedContacts.includes(contact.id)
                        ? darkMode ? "bg-green-500/10" : "bg-green-50"
                        : darkMode ? "hover:bg-zinc-700/50" : "hover:bg-gray-50"
                      }`}>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        selectedContacts.includes(contact.id)
                          ? "bg-green-500 border-nomoz-orange text-white"
                          : darkMode ? "border-zinc-600" : "border-gray-300"
                      }`}>
                        {selectedContacts.includes(contact.id) && <span className="text-xs">✓</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-gray-900"}`}>{contact.nome}</p>
                        <p className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>{contact.telefone}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {contact.total_enviadas > 0 && (
                          <span className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`} title="Taxa de entrega das mensagens">
                            📩 {contact.total_entregues || 0}/{contact.total_enviadas}
                          </span>
                        )}
                        {contact.lead_temperature && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            contact.lead_temperature === 'quente' ? 'bg-red-500/20 text-red-400' :
                            contact.lead_temperature === 'morno' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`} title={`Lead score: ${contact.lead_score || 0}/100`}>
                            {contact.lead_temperature === 'quente' ? '🔥 Quente' : contact.lead_temperature === 'morno' ? '🟡 Morno' : '🧊 Frio'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className={`flex items-center justify-between px-6 py-2 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
                <button disabled={contactModalPage <= 1} onClick={() => loadContacts(contactSearchTerm, contactTempFilter, contactModalPage - 1)}
                  className={`text-xs px-2 py-1 rounded ${contactModalPage <= 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-700'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>← Anterior</button>
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{contactModalPage} / {Math.ceil(contactModalTotal / 50) || 1} ({contactModalTotal} contatos)</span>
                <button disabled={contactModalPage >= Math.ceil(contactModalTotal / 50)} onClick={() => loadContacts(contactSearchTerm, contactTempFilter, contactModalPage + 1)}
                  className={`text-xs px-2 py-1 rounded ${contactModalPage >= Math.ceil(contactModalTotal / 50) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-700'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Próxima →</button>
              </div>

              <div className="p-6 pt-4 flex gap-3">
                <button onClick={() => { setShowContactModal(false); setSelectedContacts([]); setContactSearchTerm(""); }}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    darkMode ? "bg-zinc-700 text-gray-300 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}>Cancelar</button>
                <button disabled={selectedContacts.length === 0}
                  onClick={() => {
                    if (editingGroup || selectedGroup?.id) { handleAddContacts(); }
                    else { setFormData({ ...formData, contatos_id: selectedContacts }); setShowContactModal(false); setSelectedContacts([]); setContactSearchTerm(""); }
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-nomoz-orange hover:bg-nomoz-orange-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {editingGroup || selectedGroup?.id ? "Adicionar" : "Confirmar"} ({selectedContacts.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="modal-overlay" onClick={() => {
            setShowImportModal(false);
            setCsvFile(null);
            setSelectedGroupOption('new');
            setNewGroupName('');
            setNewGroupDescription('');
          }}>
            <div
              className={`modal-container max-w-md ${darkMode ? "bg-zinc-800" : "bg-white"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Criar Grupo com CSV
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                    setSelectedGroupOption('new');
                    setNewGroupName('');
                    setNewGroupDescription('');
                  }}
                  className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  ×
                </button>
              </div>

              <div className="modal-body space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Arquivo CSV
                  </label>
                  <input
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain,application/vnd.ms-excel"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className={`block w-full text-sm rounded-md border py-2 px-3 ${
                      darkMode
                        ? "border-gray-600 bg-zinc-700 text-white file:bg-zinc-600 file:text-white"
                        : "border-gray-300 bg-white text-gray-900 file:bg-gray-50 file:text-gray-700"
                    } file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium hover:file:bg-gray-100`}
                  />
                </div>

                {/* Informações do Grupo */}
                <div className={`border-t pt-4 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                  <label className={`block text-sm font-medium mb-3 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Informações do Grupo
                  </label>

                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Nome do grupo"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      className={`block w-full rounded-md border py-2 px-3 text-sm ${
                        darkMode
                          ? "border-gray-600 bg-zinc-700 text-white placeholder-gray-400"
                          : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Descrição (opcional)"
                      value={newGroupDescription}
                      onChange={(e) => setNewGroupDescription(e.target.value)}
                      className={`block w-full rounded-md border py-2 px-3 text-sm ${
                        darkMode
                          ? "border-gray-600 bg-zinc-700 text-white placeholder-gray-400"
                          : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                </div>

                <div className={`p-4 rounded-md ${darkMode ? "bg-zinc-700" : "bg-blue-50"}`}>
                  <h4 className={`text-sm font-medium mb-2 ${darkMode ? "text-blue-400" : "text-blue-800"}`}>
                    Formato esperado:
                  </h4>
                  <div className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                    <p>• Primeira linha: cabeçalhos (nome, telefone)</p>
                    <p>• Exemplo: nome,telefone</p>
                    <p>• Exemplo: João Silva,11999999999</p>
                  </div>
                </div>
              </div>

              {/* Consentimento */}
              <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer mx-4 mb-2 ${darkMode ? 'border-gray-600 bg-zinc-700' : 'border-gray-200 bg-gray-50'}`}>
                <input type="checkbox" checked={consentimentoDeclarado} onChange={e => setConsentimentoDeclarado(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded text-nomoz-orange focus:ring-nomoz-orange" />
                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Declaro que possuo consentimento prévio de todos os contatos para receber comunicações.
                </span>
              </label>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                    setSelectedGroupOption('new');
                    setNewGroupName('');
                    setNewGroupDescription('');
                  }}
                  className={`modal-button modal-button-secondary ${
                    darkMode
                      ? "border-gray-600 text-white hover:bg-zinc-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!csvFile) {
                      toast.error("Selecione um arquivo CSV");
                      return;
                    }
                    if (!newGroupName.trim()) {
                      toast.error("Digite o nome do grupo");
                      return;
                    }

                    setImporting(true);
                    try {
                      const text = await csvFile.text();
                      const lines = text.split('\n').filter(line => line.trim());

                      if (lines.length < 2) {
                        toast.error("Arquivo CSV deve ter pelo menos um cabeçalho e uma linha de dados");
                        setImporting(false);
                        return;
                      }

                      // Detectar delimitador
                      const headerLine = lines[0];
                      const commaSplit = headerLine.split(',');
                      const semicolonSplit = headerLine.split(';');
                      const delimiter = (semicolonSplit.length > commaSplit.length) ? ';' : ',';

                      const headers = headerLine.split(delimiter).map(h => h.trim().toLowerCase());
                      const nomeIndex = headers.findIndex(h => h.includes('nome'));
                      const telefoneIndex = headers.findIndex(h => h.includes('telefone') || h.includes('phone'));

                      if (nomeIndex === -1 || telefoneIndex === -1) {
                        toast.error("CSV deve conter colunas 'nome' e 'telefone'");
                        setImporting(false);
                        return;
                      }

                      // Validar algumas linhas
                      const erros = [];
                      const telefonesVistos = new Set();
                      for (let i = 1; i < Math.min(lines.length, 6); i++) {
                        const rawValues = lines[i].split(delimiter);
                        const values = rawValues.map(v => v.trim().replace(/"/g, ''));

                        if (values.length >= Math.max(nomeIndex + 1, telefoneIndex + 1)) {
                          const nome = (values[nomeIndex] || '').trim();
                          const telefoneBruto = (values[telefoneIndex] || '').trim();
                          const telefone = telefoneBruto.replace(/\D/g, '');

                          if (!nome) {
                            erros.push(`Linha ${i + 1}: nome vazio.`);
                          }
                          if (!/^(?!0+$)\d{10,13}$/.test(telefone)) {
                            erros.push(`Linha ${i + 1}: telefone inválido (${telefoneBruto}).`);
                          }
                          if (telefonesVistos.has(telefone)) {
                            erros.push(`Linha ${i + 1}: telefone duplicado (${telefone}).`);
                          }
                          telefonesVistos.add(telefone);
                        }
                      }

                      if (erros.length) {
                        toast.error(`Erros no CSV:\n${erros.join('\n')}`);
                        setImporting(false);
                        return;
                      }
                      
                      const groupData = {
                        nome: newGroupName,
                        descricao: newGroupDescription,
                        status: true,
                        cliente_id: user.cliente_id,
                        usuario_id: user.id,
                        csvData: text,
                        consentimento_declarado: consentimentoDeclarado,
                      };

                      setShowImportModal(false);

                      const response = await groupsAPI.create(groupData);
                      
                      if (response.data.csvImport) {
                        const { results } = response.data.csvImport;
                        toast.success(
                          `Grupo criado! ${results.adicionados_ao_grupo} contatos adicionados.`
                        );
                      } else {
                        toast.success("Grupo criado com sucesso!");
                      }
                      
                      setCsvFile(null);
                      setNewGroupName('');
                      setNewGroupDescription('');
                      loadGroups();
                    } catch (error) {
                      toast.error(error.response?.data?.message || "Erro ao criar grupo");
                      setShowImportModal(true);
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!csvFile || !newGroupName.trim() || importing}
                  className="modal-button gradient-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? "Criando..." : "Criar Grupo"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Progress Modal */}
        {importing && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
            <div className={`rounded-lg p-8 max-w-md w-full mx-4 ${darkMode ? "bg-zinc-800" : "bg-white"}`}>
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-nomoz-orange mx-auto mb-4"></div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Criando Grupo...
                </h3>
                <p className={`text-sm mb-4 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Processando contatos do CSV
                </p>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setDeleteModal(false)}>
            <div className={`w-full max-w-sm rounded-2xl shadow-xl p-6 ${darkMode ? "bg-zinc-800" : "bg-white"}`} onClick={(e) => e.stopPropagation()}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${darkMode ? "bg-red-500/20" : "bg-red-50"}`}>
                <Trash2 className="h-5 w-5 text-red-500" />
              </div>
              <h3 className={`text-center text-lg font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}>Excluir Segmento</h3>
              <p className={`text-center text-sm mb-6 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Essa ação não pode ser desfeita. Os contatos não serão excluídos.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteModal(false)}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    darkMode ? "bg-zinc-700 text-gray-300 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}>Cancelar</button>
                <button onClick={handleDeleteGroup}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors">
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Groups;
